import { Package } from 'lucide-react';
import styled from 'styled-components';

import { Button } from '../../../../../components/atoms/button/button';
import { EmptyState } from '../../../../../components/molecules/empty-state/empty-state';
import { ErrorState } from '../../../../../components/molecules/error-state/error-state';
import { Pagination } from '../../../../../components/molecules/pagination/pagination';
import { useClienteProdutos } from '../../../../../hooks/use-cliente-produtos';
import { ClienteDetail } from '../../../../../types/cliente';
import { ProdutosCards } from './produtos/produtos-cards';
import { ProdutoDetailDrawer } from './produtos/produto-detail-drawer';
import { ProdutosFilters } from './produtos/produtos-filters';
import { ProdutosTable } from './produtos/produtos-table';

interface Props {
  cliente: ClienteDetail;
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const TableWrapper = styled.div`
  display: block;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: none;
  }
`;

const CardsWrapper = styled.div`
  display: none;

  @media (max-width: ${({ theme }) => theme.breakpoints.tablet}) {
    display: block;
  }
`;

const Skeleton = styled.div`
  height: 56px;
  border-radius: ${({ theme }) => theme.radius.md};
  background: linear-gradient(
    90deg,
    ${({ theme }) => theme.colors.bgMuted} 0%,
    ${({ theme }) => theme.colors.bgSurface} 50%,
    ${({ theme }) => theme.colors.bgMuted} 100%
  );
  background-size: 200% 100%;
  animation: shimmer 1.4s linear infinite;

  @keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
  }

  & + & {
    margin-top: 8px;
  }
`;

export const ProdutosTab = ({ cliente }: Props) => {
  const {
    items,
    page,
    limit,
    total,
    totalPages,
    filters,
    status,
    error,
    setFilters,
    clearFilters,
    setPage,
    setLimit,
    refresh,
    selectedDetail,
    detailStatus,
    detailError,
    openDrawer,
    closeDrawer,
  } = useClienteProdutos(cliente.id);

  const isLoading = status === 'loading';
  const hasError = status === 'error';
  const isEmpty = !isLoading && !hasError && items.length === 0;
  const drawerOpen =
    selectedDetail != null ||
    detailStatus === 'loading' ||
    detailStatus === 'error';

  return (
    <Wrapper role="tabpanel" aria-labelledby="cliente-tab-produtos">
      <ProdutosFilters
        initialValues={filters}
        onApply={setFilters}
        onClear={clearFilters}
      />

      {isLoading && (
        <div data-testid="produtos-loading">
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
          <Skeleton />
        </div>
      )}

      {hasError && (
        <ErrorState
          title="Não foi possível carregar os produtos"
          description={error ?? 'Tente novamente em alguns instantes.'}
          action={
            <Button variant="secondary" onClick={() => refresh()}>
              Tentar novamente
            </Button>
          }
        />
      )}

      {isEmpty && (
        <EmptyState
          icon={<Package size={26} />}
          title="Este cliente não tem produtos cadastrados"
          description="Tente ajustar os filtros ou aguarde novos cadastros."
          action={
            <Button variant="secondary" onClick={clearFilters}>
              Limpar filtros
            </Button>
          }
        />
      )}

      {!isLoading && !hasError && items.length > 0 && (
        <>
          <TableWrapper>
            <ProdutosTable items={items} onOpen={openDrawer} />
          </TableWrapper>
          <CardsWrapper>
            <ProdutosCards items={items} onOpen={openDrawer} />
          </CardsWrapper>
          <Pagination
            page={page}
            limit={limit}
            total={total}
            totalPages={totalPages}
            onPageChange={setPage}
            onLimitChange={setLimit}
          />
        </>
      )}

      <ProdutoDetailDrawer
        isOpen={drawerOpen}
        cliente={cliente}
        detail={selectedDetail}
        status={detailStatus}
        errorMessage={detailError}
        onClose={closeDrawer}
      />
    </Wrapper>
  );
};
